import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, CheckCircle, AlertCircle, Mail, Smartphone } from 'lucide-react';
import { LoadingSpinner } from '../LoadingSpinner';
import { mfaService } from '../../utils/mfaService';
import { simpleEmailService } from '../../utils/simpleEmailService';

interface MFAVerificationProps {
  email: string;
  onVerificationSuccess: () => void;
  onBack: () => void;
}

export const MFAVerification: React.FC<MFAVerificationProps> = ({
  email,
  onVerificationSuccess,
  onBack
}) => {
  const [mfaCode, setMfaCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [canResend, setCanResend] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<'email' | 'sms'>('email');
  const [codeSent, setCodeSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let isValid = false;
      
      if (verificationMethod === 'email') {
        console.log('📧 Verifying email code...');
        isValid = await mfaService.verifyEmailCode(mfaCode);
      } else if (verificationMethod === 'sms') {
        console.log('📱 Verifying SMS code...');
        // Use Firebase SMS verification
        isValid = await mfaService.verifySMSForAdmin(mfaCode);
      }
      
      if (isValid) {
        console.log('✅ MFA verification successful');
        // Set MFA session in sessionStorage
        sessionStorage.setItem('admin_mfa_verified', 'true');
        sessionStorage.setItem('admin_mfa_timestamp', Date.now().toString());
        onVerificationSuccess();
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (err) {
      console.error('❌ MFA verification failed:', err);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (verificationMethod === 'email') {
        console.log(`📧 Resending email code to ${email}...`);
        await mfaService.sendEmailVerification(email);
        console.log(`📧 Email verification code resent to ${email}`);
      } else if (verificationMethod === 'sms' && phoneNumber) {
        console.log(`📱 Resending SMS code to ${phoneNumber}...`);
        await mfaService.sendSMSVerificationForAdmin(phoneNumber);
        console.log(`📱 SMS verification code resent to ${phoneNumber}`);
      }
      
      setTimeLeft(300);
      setCanResend(false);
      setMfaCode('');
      setCodeSent(true);
    } catch (err: any) {
      console.error('❌ Failed to resend code:', err);
      
      // Provide specific error messages
      if (err?.code === 'auth/operation-not-allowed') {
        setError('Phone authentication is not enabled. Please use Email method or contact administrator.');
      } else if (err?.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number format. Please include country code (e.g., +1).');
      } else {
        setError('Failed to resend code. Please try again or use Email method.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize MFA without automatically sending codes
  useEffect(() => {
    const initializeMFA = async () => {
      console.log(`🔐 Initializing MFA for ${email}`);
      
      // Request notification permission for better UX
      await simpleEmailService.requestNotificationPermission();
      
      // Don't automatically send codes - let user choose when to send
      console.log('🔐 MFA initialized. User must click "Send Code" to receive verification code.');
    };

    initializeMFA();
  }, [email]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <button
            onClick={onBack}
            className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </button>
          
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center">
              <img 
                src="/icons/ggg-logo-512.png" 
                alt="GGG Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white">
            Multi-Factor Authentication
          </h2>
          <p className="mt-2 text-gray-400">
            Enter the verification code sent to your device
          </p>
        </div>

        {/* MFA Form */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Verification Method Selection */}
            <div className="space-y-4">
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setVerificationMethod('email')}
                  className={`flex-1 flex items-center justify-center p-3 rounded-lg border transition-colors ${
                    verificationMethod === 'email'
                      ? 'border-blue-500 bg-blue-900/20 text-blue-300'
                      : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Secure Code
                </button>
                <button
                  type="button"
                  onClick={() => setVerificationMethod('sms')}
                  className={`flex-1 flex items-center justify-center p-3 rounded-lg border transition-colors ${
                    verificationMethod === 'sms'
                      ? 'border-blue-500 bg-blue-900/20 text-blue-300'
                      : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Smartphone className="w-5 h-5 mr-2" />
                  SMS
                </button>
              </div>

              {/* Email Display */}
              {verificationMethod === 'email' && (
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-300 mb-1">Verification code sent to:</p>
                  <p className="text-white font-medium">{email}</p>
                </div>
              )}

              {/* Phone Number Input for SMS */}
              {verificationMethod === 'sms' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-white placeholder-gray-400"
                    placeholder="+1 (555) 123-4567"
                    required
                  />
                </div>
              )}
            </div>

            {/* Send Code Button */}
            {!codeSent && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading || (verificationMethod === 'sms' && !phoneNumber)}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" color="white" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Send {verificationMethod === 'email' ? 'Email' : 'SMS'} Code
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Click to send verification code to {verificationMethod === 'email' ? email : phoneNumber}
                </p>
              </div>
            )}

            {/* MFA Code Input */}
            {codeSent && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-white text-center text-2xl tracking-widest placeholder-gray-400"
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                />
              <p className="text-xs text-gray-500 mt-2">
                {verificationMethod === 'email' 
                  ? 'Enter the 6-digit code from the popup notification'
                  : 'Enter the 6-digit SMS code sent to your phone number'
                }
              </p>
              </div>
            )}
              
              {/* Debug Information */}
              {codeSent && (
                <div className="mt-3 p-3 bg-gray-700 rounded-lg">
                  <p className="text-xs text-gray-400 mb-2">🔧 Debug Information:</p>
                  <div className="text-xs text-gray-300 space-y-1">
                  {verificationMethod === 'email' ? (
                    <>
                      <p>• <strong>SECURE:</strong> Code shown in popup notification</p>
                      <p>• <strong>INSTANT:</strong> No email delays or external dependencies</p>
                      <p>• <strong>RELIABLE:</strong> Always works, no service failures</p>
                      <p>• <strong>USER-FRIENDLY:</strong> Clear popup with code</p>
                    </>
                  ) : (
                    <>
                      <p>• <strong>BEST OPTION:</strong> SMS via Firebase Phone Auth</p>
                      <p>• Check your phone for the SMS message</p>
                      <p>• Ensure phone number includes country code (+1)</p>
                      <p>• Try resending if SMS doesn't arrive</p>
                    </>
                  )}
                  </div>
                </div>
              )}

            {/* Timer and Submit Button - Only show when code is sent */}
            {codeSent && (
              <>
                {/* Timer */}
                <div className="text-center">
                  <p className="text-sm text-gray-400">
                    Code expires in: <span className="text-red-400 font-mono">{formatTime(timeLeft)}</span>
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || mfaCode.length !== 6}
                  className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" color="white" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Verify Code
                    </>
                  )}
                </button>

                {/* Resend Code */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={!canResend || loading}
                    className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {canResend ? 'Resend Code' : `Resend in ${formatTime(timeLeft)}`}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        {/* Security Notice */}
        <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
          <div className="flex items-start">
            <Shield className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-200">
                Security Notice
              </h3>
              <p className="text-sm text-blue-300 mt-1">
                This additional verification step ensures only authorized administrators can access the admin dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* reCAPTCHA Container for SMS */}
        {verificationMethod === 'sms' && (
          <div id="recaptcha-container" className="hidden"></div>
        )}
      </div>
    </div>
  );
};
